import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    styled,
    Chip,
    Divider,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    TextField,
    InputAdornment
} from '@mui/material';
import { CloudUpload, ArrowBack, Visibility, ExpandMore, ExpandLess, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { 
    uploadQuestionnaire, 
    getReviewDefinitions,
    ReviewDefinition,
    getReviewEntityTypes,
    ReviewEntityType,
    getAvailableQuestionnaires,
    AvailableQuestionnaire
} from '../../services/api';


interface Entity {
    id: string;
    name: string;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const QuestionnaireManagement = () => {
    const navigate = useNavigate();
    const [reviewDefinitions, setReviewDefinitions] = useState<ReviewDefinition[]>([]);
    const [reviewEntityTypes, setReviewEntityTypes] = useState<ReviewEntityType[]>([]);
    const [availableQuestionnaires, setAvailableQuestionnaires] = useState<AvailableQuestionnaire[]>([]);
    const [selectedReviewDefinition, setSelectedReviewDefinition] = useState<number | ''>('');
    const [selectedEntityType, setSelectedEntityType] = useState<number | ''>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(false);
    const [newQuestionnaireExpanded, setNewQuestionnaireExpanded] = useState(true);
    
    // Filter states for Available Questionnaires table
    const [filterDefinition, setFilterDefinition] = useState<string>('');
    const [filterEntityType, setFilterEntityType] = useState<string>('');
    const [filterFrequency, setFilterFrequency] = useState<string>('');
    const [filterCategory, setFilterCategory] = useState<string>('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [definitionsResponse, entityTypesResponse, availableResponse] = await Promise.all([
                    getReviewDefinitions(),
                    getReviewEntityTypes(),
                    getAvailableQuestionnaires()
                ]);
                setReviewDefinitions(definitionsResponse.data);
                setReviewEntityTypes(entityTypesResponse.data);
                setAvailableQuestionnaires(availableResponse.data);
            } catch (err) {
                setError('Failed to load review definitions, entity types, or available questionnaires');
            }
        };

        fetchData();
    }, []);

    const filteredQuestionnaires = useMemo(() => {
        return availableQuestionnaires.filter(questionnaire => {
            const definitionMatch = filterDefinition === '' || 
                questionnaire.review_definition.display_label.toLowerCase().includes(filterDefinition.toLowerCase());
            const entityTypeMatch = filterEntityType === '' || 
                questionnaire.review_entity_type.display_label.toLowerCase().includes(filterEntityType.toLowerCase());
            const frequencyMatch = filterFrequency === '' || 
                (questionnaire.review_definition.review_frequency?.display_label || '').toLowerCase().includes(filterFrequency.toLowerCase());
            const categoryMatch = filterCategory === '' || 
                questionnaire.review_entity_type.category.toLowerCase().includes(filterCategory.toLowerCase());
            return definitionMatch && entityTypeMatch && frequencyMatch && categoryMatch;
        });
    }, [availableQuestionnaires, filterDefinition, filterEntityType, filterFrequency, filterCategory]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
                file.type !== 'application/vnd.ms-excel') {
                setError('Please upload only Excel files (.xlsx or .xls)');
                return;
            }
            setSelectedFile(file);
            setError('');
        }
    };

    const getSelectedDefinition = () => {
        return reviewDefinitions.find(def => def.id === selectedReviewDefinition);
    };

    const getApplicableEntityTypes = () => {
        const selectedDef = getSelectedDefinition();
        if (!selectedDef || !selectedDef.applicable_review_entity_types) {
            return [];
        }
        return selectedDef.applicable_review_entity_types;
    };

    const handleUpload = async () => {
        if (!selectedReviewDefinition || !selectedEntityType || !selectedFile) {
            setError('Please select a review definition, entity type, and a file');
            return;
        }

        setError('');
        setSuccess('');
        setLoading(true);
        setUploadProgress(true);

        const selectedDef = getSelectedDefinition();
        const formData = new FormData();
        formData.append('review_definition_id', selectedReviewDefinition.toString());
        formData.append('review_entity_type_id', selectedEntityType.toString());
        formData.append('questionnaire_name', selectedDef?.name || '');
        formData.append('file', selectedFile);

        try {
            await uploadQuestionnaire(formData);
            setSuccess('Questionnaire uploaded successfully');
            // Reset form
            setSelectedReviewDefinition('');
            setSelectedEntityType('');
            setSelectedFile(null);
            setUploadProgress(false);
            // Refresh the available questionnaires list
            const availableResponse = await getAvailableQuestionnaires();
            setAvailableQuestionnaires(availableResponse.data);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.response?.data?.message || 'Failed to upload questionnaire');
            setUploadProgress(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
  <TitleBar />
  <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          aria-label="Back"
          onClick={() => navigate('/home')}
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            p: 1,
            backgroundColor: '#f5f5f5',
            '&:hover': {
              backgroundColor: '#e0e0e0',
            },
            mr: 2,
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">Questionnaire Management</Typography>
      </Box>

      {/* New Questionnaire Section */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              mb: newQuestionnaireExpanded ? 2 : 0
            }}
            onClick={() => setNewQuestionnaireExpanded(!newQuestionnaireExpanded)}
          >
            <Typography variant="h6" sx={{ color: '#1976d2' }}>
              ➕ New Questionnaire
            </Typography>
            <IconButton size="small">
              {newQuestionnaireExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          <Collapse in={newQuestionnaireExpanded}>
            <Box sx={{ pt: 1 }}>
                    {(error || success) && (
                        <Alert 
                            severity={error ? 'error' : 'success'} 
                            sx={{ mb: 3 }}
                            onClose={() => error ? setError('') : setSuccess('')}
                        >
                            {error || success}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel>Review Definition *</InputLabel>
                            <Select
                                value={selectedReviewDefinition}
                                label="Review Definition *"
                                onChange={(e) => {
                                    setSelectedReviewDefinition(e.target.value as number);
                                    setSelectedEntityType(''); // Reset entity type when definition changes
                                }}
                                disabled={loading}
                            >
                                {reviewDefinitions.map((def) => (
                                    <MenuItem key={def.id} value={def.id}>
                                        {def.display_label} ({def.review_frequency?.display_label || 'GATED'})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedReviewDefinition && getSelectedDefinition() && (
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    p: 2.5, 
                                    backgroundColor: '#f8f9fa',
                                    borderColor: '#dee2e6'
                                }}
                            >
                                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mb: 2, fontWeight: 600 }}>
                                    {getSelectedDefinition()?.display_label}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {getSelectedDefinition()?.description && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Description
                                            </Typography>
                                            <Typography variant="body2">
                                                {getSelectedDefinition()?.description}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 3 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Review Type
                                            </Typography>
                                            <Chip 
                                                label={getSelectedDefinition()?.review_type?.display_label || 'N/A'}
                                                size="small"
                                                color="primary"
                                                sx={{ fontWeight: 500 }}
                                            />
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Review Frequency
                                            </Typography>
                                            <Chip 
                                                label={getSelectedDefinition()?.review_frequency?.display_label || 'N/A'}
                                                size="small"
                                                color="secondary"
                                                sx={{ fontWeight: 500 }}
                                            />
                                        </Box>
                                    </Box>

                                    {getApplicableEntityTypes().length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Select Entity Type * (Click to select)
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {getApplicableEntityTypes().map((entityType) => (
                                                    <Chip
                                                        key={entityType.id}
                                                        label={entityType.display_label}
                                                        size="medium"
                                                        clickable
                                                        onClick={() => setSelectedEntityType(entityType.id)}
                                                        color={selectedEntityType === entityType.id ? 'primary' : 'default'}
                                                        variant={selectedEntityType === entityType.id ? 'filled' : 'outlined'}
                                                        sx={{ 
                                                            borderColor: selectedEntityType === entityType.id ? undefined : '#6c757d',
                                                            color: selectedEntityType === entityType.id ? undefined : '#495057',
                                                            fontWeight: selectedEntityType === entityType.id ? 600 : 400,
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: selectedEntityType === entityType.id ? undefined : '#e9ecef',
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                            {selectedEntityType && (
                                                <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                                                    ✓ Selected: {getApplicableEntityTypes().find(et => et.id === selectedEntityType)?.display_label}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        )}

                        <Box>
                            {!selectedEntityType && selectedReviewDefinition && (
                                <Typography variant="caption" color="warning.main" sx={{ mb: 2, display: 'block' }}>
                                    ⚠ Please select an entity type above before uploading the file
                                </Typography>
                            )}
                            
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUpload />}
                                    disabled={loading || !selectedReviewDefinition || !selectedEntityType}
                                    sx={{ minWidth: '200px', height: '56px' }}
                                >
                                    {selectedFile ? selectedFile.name : 'Upload Excel File *'}
                                    <VisuallyHiddenInput type="file" onChange={handleFileChange} accept=".xlsx,.xls" />
                                </Button>
                                
                                <Button
                                    variant="contained"
                                    onClick={handleUpload}
                                    disabled={loading || !selectedFile || !selectedReviewDefinition || !selectedEntityType}
                                    sx={{ minWidth: '200px', height: '56px' }}
                                >
                                    {loading ? 'Processing...' : 'Upload Questionnaire'}
                                </Button>
                            </Box>
                            
                            {uploadProgress && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
                                    <CircularProgress size={24} />
                                    <Typography>Uploading and processing questionnaire...</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Available Questionnaires - Table */}
      {availableQuestionnaires.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2.5, pb: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ color: '#1976d2' }}>
                  📋 Available Questionnaires
                </Typography>
                <Chip 
                  label={availableQuestionnaires.length}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TextField
                        placeholder="Filter by definition"
                        value={filterDefinition}
                        onChange={(e) => setFilterDefinition(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Filter by entity type"
                        value={filterEntityType}
                        onChange={(e) => setFilterEntityType(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Filter by frequency"
                        value={filterFrequency}
                        onChange={(e) => setFilterFrequency(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Filter by category"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>Review Definition</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>Entity Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>Frequency</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>Category</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#424242' }}>Questions</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#424242' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredQuestionnaires.map((questionnaire, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        '&:last-child td': { borderBottom: 0 }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                          {questionnaire.review_definition.display_label}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={questionnaire.review_entity_type.display_label}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={questionnaire.review_definition.review_frequency?.display_label || 'N/A'}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={questionnaire.review_entity_type.category}
                          size="small"
                          sx={{ borderColor: '#757575', color: '#757575' }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={questionnaire.question_count}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => navigate(`/view-questionnaire/${questionnaire.review_definition_id}/${questionnaire.review_entity_type_id}`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
            </Container>
        </Box>
    );
};

export default QuestionnaireManagement;